/**
 * Triangular
 * ----------
 * 
 * June 2026
 */

import { InputNumber } from "primereact/inputnumber";

/**
 * Distribution component for editing the parameters of a triangular distribution.
 * 
 * @param {String} elementType      - Type of element tied to distribution. (actions, resources, entity_types)
 * @param {Object} element          - Attributes for an element being edited.
 * @param {Object} attr             - Distribution parameters tied with element attribute.
 * @param {Function} distUpdater    - Sets which distribution is currently being used.
 * @param {Function} warningUpdater - Updates warnings for an element parameter.
 * @param {Object} configWarnings   - Verification warnings for model elements.
 */
function Triangular({ elementType, element, attr, distUpdater, warningUpdater, configWarnings }) {
    const triangularWarnings = {
        'lower_bound': function(newRange) { return !newRange.lower_bound || !(newRange.lower_bound < newRange.upper_bound) || !(newRange.lower_bound <= newRange.mode) || !(newRange.upper_bound >= newRange.mode) ? `${attr} - Lower bound must be less than upper bound and mode` : null },
        'upper_bound': function(newRange) { return !newRange.upper_bound || !(newRange.upper_bound > newRange.lower_bound) || !(newRange.lower_bound <= newRange.mode) || !(newRange.upper_bound >= newRange.mode) ? `${attr} - Upper bound must be greater than lower bound and mode` : null },
        'mode': function(newRange) { return !newRange.mode || !(newRange.mode >= newRange.lower_bound) || !(newRange.mode <= newRange.upper_bound) ? `${attr} - Mode must be between lower and upper bounds` : null}
    }

    /**
     * Updates the triangular distribution parameters for an attribute.
     * 
     * Example parameters:
     *  - lower bound
     *  - upper bound
     *  - mode
     * 
     * @param {String} triangParam  - Triangular parameter being updated.
     * @param {float} newValue      - Updated value for the triangular parameter. 
     */
    const handleTriangular = (triangParam, newValue) => {
        const newTriangular = {...element[attr].attr, [triangParam]: newValue};
        const newDist = {...element[attr], attr: newTriangular};

        warningUpdater(element.gid, attr, triangularWarnings[triangParam](newTriangular));

        // console.log("New triangular distribution: ", newDist);
        distUpdater(attr, newDist);
    }

    return (
        <div>
            <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ marginRight: '0.5rem' }}>Lower Bound:</label>
                <InputNumber value={element[attr].attr.lower_bound} onValueChange={(event) => handleTriangular('lower_bound', event.target.value)} invalid={!!configWarnings[elementType]?.[element.gid]?.[attr]} inputStyle={{ width: '110px' }}/>
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ marginRight: '0.5rem' }}>Upper Bound:</label>
                <InputNumber value={element[attr].attr.upper_bound} onValueChange={(event) => handleTriangular('upper_bound', event.target.value)} invalid={!!configWarnings[elementType]?.[element.gid]?.[attr]} inputStyle={{ width: '110px' }}/>
            </div>
            <div>
                <label style={{ marginRight: '0.5rem' }}>Mode:</label>
                <InputNumber value={element[attr].attr.mode} onValueChange={(event) => handleTriangular('mode', event.target.value)} invalid={!!configWarnings[elementType]?.[element.gid]?.[attr]} inputStyle={{ width: '110px' }}/>
            </div>
        </div>
    )
}

export default Triangular;